import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// import { CustomFieldData } from "../components/CreateCustomFieldModal";
import FieldMappingTable, { MappingRow } from "../components/FieldMappingTable";
import { HubSpotProperty } from "../types/api";
import { VeracrossField } from "../types/veracrossFields";
import { toast } from "../lib/toast";
import { apiClient } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const ParentFieldMapping: FC = () => {
  const navigate = useNavigate();
  const { portalId, encryptedUserId, isLoading: authLoading, error: authError } = useAuth();

  const [hubspotProperties, setHubspotProperties] = useState<HubSpotProperty[]>(
    []
  );
  const [veracrossFields, setVeracrossFields] = useState<VeracrossField[]>([]);
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (authLoading) {
        return;
      }

      if (!portalId || !encryptedUserId) {
        setError(authError || "Portal ID or User ID is missing");
        setLoading(false);
        return;
      }

      // Prevent duplicate API calls
      if (hasFetchedData.current) {
        return;
      }
      hasFetchedData.current = true;

      try {
        // Fetch HubSpot contact properties and Veracross fields in parallel
        const [hubspotResponse, veracrossResponse, mappingsResponse] = await Promise.all([
          apiClient.getHubSpotProperties(portalId, encryptedUserId, "contacts"),
          apiClient.getVeracrossFields(encryptedUserId, "relative"),
          apiClient.getMappings(portalId, encryptedUserId, 'relative')
        ]);

        if (hubspotResponse.success && hubspotResponse.properties) {
          setHubspotProperties(hubspotResponse.properties);
        } else {
          setError(hubspotResponse.error || "Failed to fetch HubSpot properties");
        }

        if (veracrossResponse.success && veracrossResponse.fields) {
          setVeracrossFields(veracrossResponse.fields);
        }
        
        if (mappingsResponse.success && mappingsResponse.mappings) {
          // Convert mappings to MappingRow format
          // Filter out mappings where HubSpot field is blank/empty, but keep them in default mapping
          const mappingRows: MappingRow[] = mappingsResponse.mappings
            .filter(mapping => mapping.hubspotFieldName && mapping.hubspotFieldName.trim() !== '')
            .map((mapping, index) => ({
              id: `mapping_${index + 1}`,
              mappingId: mapping.mappingId, // Include database ID for existing mappings
              hubspotField: mapping.hubspotFieldName,
              hubspotFieldName: mapping.hubspotFieldName,
              hubspotFieldLabel: mapping.hubspotFieldLabel,
              fieldType: mapping.fieldType,
              veracrossField: mapping.veracrossFieldName,
              veracrossFieldName: mapping.veracrossFieldName,
              veracrossFieldLabel: mapping.veracrossFieldLabel,
              default_value: null,
              transformationType: undefined,
            }));
          setMappingRows(mappingRows);
        } else {
          // Fallback to empty mappings if API fails
          setMappingRows([]);
        }

        setLoading(false);
      } catch (error) {
        setError("Failed to fetch data");
        setLoading(false);
      }
    };

    fetchData();
  }, [portalId, authLoading, authError]);

  const handleBack = () => {
    navigate(
      `/applicant-field-mapping?userId=${encodeURIComponent(encryptedUserId || "")}`
    );
  };

  const handleNext = async () => {
    if (!portalId || !encryptedUserId) {
      toast({
        title: "Error",
        description: "Missing required information to save mappings",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      // Convert mapping rows to the format expected by the API
      const mappings = mappingRows.map(row => ({
        hubspotFieldName: row.hubspotFieldName,
        hubspotFieldLabel: row.hubspotFieldLabel,
        veracrossFieldName: row.veracrossFieldName,
        veracrossFieldLabel: row.veracrossFieldLabel,
        fieldType: row.fieldType || 'string',
      }));

      const response = await apiClient.saveMappings(portalId, encryptedUserId, {
        objectType: 'relative',
        mappings,
      });

      if (response.success) {
        // Store mapping data in localStorage for the complete screen
        localStorage.setItem('relativeMappings', JSON.stringify(mappingRows));
        
        toast({
          title: "Success",
          description: "Parent field mappings saved successfully",
        });
        navigate(
          `/application-field-mapping?userId=${encodeURIComponent(encryptedUserId || "")}`
        );
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to save mappings",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save mappings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // const handleCustomFieldCreated = async (fieldData: CustomFieldData) => {
  //   // Refresh HubSpot properties to include the newly created custom field
  //   try {
  //     const hubspotResponse = await apiClient.getHubSpotProperties(userId!, "contacts");
  //     if (hubspotResponse.success && hubspotResponse.properties) {
  //       setHubspotProperties(hubspotResponse.properties);
  //       toast({
  //         title: "Custom field created",
  //         description: `Field "${fieldData.propertyName}" has been created and is now available for mapping.`,
  //       });
  //     } else {
  //       toast({
  //         title: "Field created but refresh failed",
  //         description: "The custom field was created but we couldn't refresh the field list. Please refresh the page.",
  //         variant: "destructive",
  //       });
  //     }
  //   } catch (error) {
  //     toast({
  //       title: "Field created but refresh failed",
  //       description: "The custom field was created but we couldn't refresh the field list. Please refresh the page.",
  //       variant: "destructive",
  //     });
  //   }
  // };

  if (!portalId) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Portal ID is missing</p>
            <p className="text-gray-500">
              Please return to the previous step and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">
              Error loading HubSpot properties
            </p>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading mapping interface...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FieldMappingTable
        title="Parent field mapping"
        description="Mapping parent data fields ensures that information seamlessly moves between Veracross and HubSpot, maintaining accuracy and consistency for your HubSpot parent contacts."
        category="parents"
        hubspotProperties={hubspotProperties}
        veracrossFields={veracrossFields}
        mappingRows={mappingRows}
        onMappingChange={setMappingRows}
        onBack={handleBack}
        onNext={handleNext}
        showNotification={showNotification}
        onNotificationClose={() => setShowNotification(false)}
        // onCustomFieldCreated={handleCustomFieldCreated}
        nextButtonText={saving ? "Saving..." : "Save & Continue"}
        nextButtonDisabled={saving}
        portalId={portalId}
        encryptedUserId={encryptedUserId || ""}
      />
      
      {/* Note about custom fields */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> New HubSpot custom fields appear here after a refresh.
        </p>
      </div>
    </div>
  );
};

export default ParentFieldMapping;
